/**
 * UI-only security restrictions during the placement test.
 * - Calls onRestrictedAction(reason) to show a message.
 * - DOES NOT dispatch or call backend by itself.
 *
 * @param {(reason: string) => void} onRestrictedAction
 * @returns {() => void} cleanup
 */
export const applyTestRestrictions = (onRestrictedAction) => {
  const blockActions = async (e, reason) => {
    e.preventDefault();
    onRestrictedAction?.(reason); // Show modal/message only
    return false;
  };

  const disableRefreshKeys = (e) => {
    if (e.key === "F5" || (e.ctrlKey && e.key.toLowerCase() === "r")) {
      blockActions(e, "Page refresh is not allowed during the test.");
    }
  };

  const blockSensitiveKeys = (e) => {
    const key = e.key?.toLowerCase?.() || "";

    // Print screen variations
    const printKeys = ["printscreen", "print screen", "printscrn", "prt sc", "prtscn", "snapshot"];
    if (printKeys.includes(key)) {
      blockActions(e, "Screenshots are not allowed during the test!");
      interfereWithScreenshot();
      try { navigator.clipboard.writeText(""); } catch {}
      return;
    }

    // Block common shortcut combos only (to avoid too many false positives)
    if (e.ctrlKey || e.metaKey || e.altKey) {
      const k = key;
      const isKnownCombo = ["a","c","v","x","s","p","tab","r","n","t","w","q"].includes(k);
      if (isKnownCombo) blockActions(e, "Shortcut keys are not allowed during the test.");
    }
  };

  const interfereWithScreenshot = () => {
    if (document.getElementById("printscreen-disruption")) return;
    const overlay = document.createElement("div");
    overlay.id = "printscreen-disruption";
    Object.assign(overlay.style, {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      zIndex: 99999,
      pointerEvents: "none",
      background: "rgba(0, 0, 0, 0.12)",
      backdropFilter: "blur(2px)",
      transition: "opacity 0.2s ease",
      opacity: "0",
    });
    document.body.appendChild(overlay);
    requestAnimationFrame(() => {
      overlay.style.opacity = "1";
    });
    setTimeout(() => overlay.remove(), 600);
  };

  const blockContextAndClipboard = (e) => {
    blockActions(e, "Copy, paste, and right-click are disabled during the test.");
  };

  const onVisibility = () => {
    if (document.visibilityState === "hidden") {
      onRestrictedAction?.("Tab switching is not allowed during the test.");
    }
  };

  // Removed passive disruptive overlay – trigger only on restricted actions.
  let passiveOverlayInterval = null;

  document.addEventListener("keydown", disableRefreshKeys);
  document.addEventListener("keydown", blockSensitiveKeys);
  document.addEventListener("contextmenu", blockContextAndClipboard);
  document.addEventListener("copy", blockContextAndClipboard);
  document.addEventListener("cut", blockContextAndClipboard);
  document.addEventListener("paste", blockContextAndClipboard);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    document.removeEventListener("keydown", disableRefreshKeys);
    document.removeEventListener("keydown", blockSensitiveKeys);
    document.removeEventListener("contextmenu", blockContextAndClipboard);
    document.removeEventListener("copy", blockContextAndClipboard);
    document.removeEventListener("cut", blockContextAndClipboard);
    document.removeEventListener("paste", blockContextAndClipboard);
    document.removeEventListener("visibilitychange", onVisibility);
    if (passiveOverlayInterval) clearInterval(passiveOverlayInterval);
  };
};
